import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import AutomationFoldersAPI from '../../api/automationRuleFolders';

let latestGetToken = 0;
let lastAppliedToken = 0;
let loadedAccountId = null;
let latestCreateToken = 0;
let latestUpdateToken = 0;
let latestDeleteToken = 0;

const routeAccountId = rootState => rootState?.route?.params?.accountId;

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    hasLoaded: false,
  },
};

export const getters = {
  getFolders(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  get: async function getFolders({ commit, rootState }) {
    const requestAccountId = routeAccountId(rootState);
    latestGetToken += 1;
    const token = latestGetToken;
    if (loadedAccountId != null && loadedAccountId !== requestAccountId) {
      commit(types.SET_AUTOMATION_FOLDERS, []);
    }
    commit(types.SET_AUTOMATION_FOLDER_UI_FLAG, {
      isFetching: true,
      hasLoaded: false,
    });
    try {
      const response = await AutomationFoldersAPI.get();
      if (routeAccountId(rootState) !== requestAccountId) {
        return;
      }
      if (token <= lastAppliedToken) {
        return;
      }
      lastAppliedToken = token;
      loadedAccountId = requestAccountId;
      commit(types.SET_AUTOMATION_FOLDERS, response.data.payload);
      commit(types.SET_AUTOMATION_FOLDER_UI_FLAG, { hasLoaded: true });
    } catch (error) {
      // Ignore error
      console.error('Failed to fetch automation folders', error);
    } finally {
      if (token === latestGetToken) {
        commit(types.SET_AUTOMATION_FOLDER_UI_FLAG, { isFetching: false });
      }
    }
  },
  create: async function createFolder({ commit, rootState }, folderObj) {
    const requestAccountId = routeAccountId(rootState);
    latestCreateToken += 1;
    const createToken = latestCreateToken;
    commit(types.SET_AUTOMATION_FOLDER_UI_FLAG, { isCreating: true });
    try {
      const response = await AutomationFoldersAPI.create(folderObj);
      if (routeAccountId(rootState) === requestAccountId) {
        commit(types.ADD_AUTOMATION_FOLDER, response.data);
      }
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      if (createToken === latestCreateToken) {
        commit(types.SET_AUTOMATION_FOLDER_UI_FLAG, { isCreating: false });
      }
    }
  },
  update: async ({ commit, rootState }, { id, ...updateObj }) => {
    const requestAccountId = routeAccountId(rootState);
    latestUpdateToken += 1;
    const updateToken = latestUpdateToken;
    commit(types.SET_AUTOMATION_FOLDER_UI_FLAG, { isUpdating: true });
    try {
      const response = await AutomationFoldersAPI.update(id, updateObj);
      if (routeAccountId(rootState) === requestAccountId) {
        commit(types.EDIT_AUTOMATION_FOLDER, response.data.payload);
      }
    } catch (error) {
      throw new Error(error);
    } finally {
      if (updateToken === latestUpdateToken) {
        commit(types.SET_AUTOMATION_FOLDER_UI_FLAG, { isUpdating: false });
      }
    }
  },
  updateOrder: async (
    { commit, rootState, state: moduleState },
    orderedFolders
  ) => {
    const requestAccountId = routeAccountId(rootState);
    latestUpdateToken += 1;
    const updateToken = latestUpdateToken;
    const previousFolders = [...moduleState.records];
    commit(types.SET_AUTOMATION_FOLDERS, orderedFolders);
    commit(types.SET_AUTOMATION_FOLDER_UI_FLAG, { isUpdating: true });
    try {
      const payload = orderedFolders.map(({ id, position }) => ({
        id,
        position,
      }));
      const response = await AutomationFoldersAPI.updateOrder(payload);
      if (
        routeAccountId(rootState) === requestAccountId &&
        updateToken === latestUpdateToken
      ) {
        commit(types.SET_AUTOMATION_FOLDERS, response.data.payload);
      }
    } catch (error) {
      if (
        routeAccountId(rootState) === requestAccountId &&
        updateToken === latestUpdateToken
      ) {
        commit(types.SET_AUTOMATION_FOLDERS, previousFolders);
      }
      throw new Error(error);
    } finally {
      if (updateToken === latestUpdateToken) {
        commit(types.SET_AUTOMATION_FOLDER_UI_FLAG, { isUpdating: false });
      }
    }
  },
  delete: async ({ commit, rootState }, id) => {
    const requestAccountId = routeAccountId(rootState);
    latestDeleteToken += 1;
    const deleteToken = latestDeleteToken;
    commit(types.SET_AUTOMATION_FOLDER_UI_FLAG, { isDeleting: true });
    try {
      await AutomationFoldersAPI.delete(id);
      if (routeAccountId(rootState) === requestAccountId) {
        commit(types.DELETE_AUTOMATION_FOLDER, id);
      }
    } catch (error) {
      throw new Error(error);
    } finally {
      if (deleteToken === latestDeleteToken) {
        commit(types.SET_AUTOMATION_FOLDER_UI_FLAG, { isDeleting: false });
      }
    }
  },
};

export const mutations = {
  [types.SET_AUTOMATION_FOLDER_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.ADD_AUTOMATION_FOLDER]: MutationHelpers.create,
  [types.SET_AUTOMATION_FOLDERS]: MutationHelpers.set,
  [types.EDIT_AUTOMATION_FOLDER]: MutationHelpers.update,
  [types.DELETE_AUTOMATION_FOLDER]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
